'use client';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { TranslatedBlogArticle } from '@/types/Blog';

type Props = {
  article: TranslatedBlogArticle;
  isOnMobile: boolean;
};

export default function BlogArticleHeaderSocialLinks({
  article,
  isOnMobile,
}: Props) {
  const pathname = usePathname();

  const baseUrl = `https://www.villatravellers.com${pathname}`;

  const subject = article?.blogArticleTranslation.title + ' - Villatravellers';
  const body = `${striptags(
    article?.blogArticleTranslation.description || '',
  )}\n\n${pathname}`;

  const urls = {
    whatsAppShareUrl: isOnMobile
      ? `whatsapp://send?text=${baseUrl}`
      : `https://web.whatsapp.com/send?text=${baseUrl}`,
    facebookShareUrl: `https://www.facebook.com/sharer/sharer.php?u=${baseUrl}`,
    linkedinShareUrl: `https://www.linkedin.com/sharing/share-offsite/?url=${baseUrl}`,
    twitterShareUrl: `https://twitter.com/intent/tweet?url=${baseUrl}&text=${subject}`,
    emailShareUrl: `mailto:?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`,
  };

  function striptags(value: string) {
    return `${value}`.replace(/(<([^>]+)>)/gi, '');
  }

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="text-blue flex-[0_0_auto] min-h-0 min-w-0 w-full">
        <Link
          className="text-gray-500 hover:text-blue"
          href={urls.facebookShareUrl}
          target="_blank">
          <figure className=" inline-block leading-[1] mx-[7px] align-middle relative">
            <svg
              width="18px"
              height="18px"
              viewBox="0 0 18 18"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg">
              <title>Facebook</title> <desc>Created with Sketch.</desc>{' '}
              <defs></defs>{' '}
              <g
                id="Symbols"
                stroke="none"
                strokeWidth="1"
                fill="none"
                fillRule="evenodd">
                <g
                  id="Footer/Desktop"
                  transform="translate(-1049.000000, -488.000000)"
                  fillRule="nonzero"
                  fill="currentColor">
                  <g id="Footer">
                    <g
                      id="social"
                      transform="translate(1049.000000, 488.000000)">
                      <path
                        d="M0.993462,18 C0.444627375,18 0,17.555097 0,17.0064761 L0,0.993454125 C0,0.44469375 0.444697125,0 0.993462,0 L17.0066081,0 C17.5552331,0 18,0.44469375 18,0.993454125 L18,17.0064761 C18,17.5551668 17.5551634,18 17.0066081,18 L12.4197394,18 L12.4197394,11.0294081 L14.7594454,11.0294081 L15.1097831,8.31284475 L12.4197394,8.31284475 L12.4197394,6.57849038 C12.4197394,5.79198263 12.638142,5.25600375 13.7659838,5.25600375 L15.2044916,5.25537487 L15.2044916,2.82565912 C14.9557073,2.792553 14.101794,2.71858875 13.108332,2.71858875 C11.0343128,2.71858875 9.61438312,3.98457225 9.61438312,6.30945338 L9.61438312,8.31284475 L7.26867075,8.31284475 L7.26867075,11.0294081 L9.61438312,11.0294081 L9.61438312,18 L0.993462,18 Z"
                        id="Facebook"></path>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          </figure>
        </Link>
        <Link
          className="text-gray-500 hover:text-blue"
          href={urls.linkedinShareUrl}
          target="_blank">
          <figure className=" inline-block leading-[1] mx-[7px] align-middle relative">
            <svg
              width="18px"
              height="18px"
              viewBox="0 0 18 18"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg">
              <title>linkedin</title> <desc>Created with Sketch.</desc>{' '}
              <g
                id="Symbols"
                stroke="none"
                strokeWidth="1"
                fill="none"
                fillRule="evenodd">
                <g
                  id="Footer/Desktop"
                  transform="translate(-1016.000000, -488.000000)"
                  fill="currentColor">
                  <g id="Footer">
                    <g
                      id="social"
                      transform="translate(1006.000000, 488.000000)">
                      <path
                        d="M14,2 C14,3.1 13.3,4 12,4 C10.8,4 10,3.1 10,2.1 C10,1 10.8,0 12,0 C13.2,0 14,0.9 14,2 Z M10,18 L14,18 L14,5 L10,5 L10,18 Z M23.6,5.2 C21.5,5.2 20.3,6.4 19.8,7.2 L19.7,7.2 L19.5,5.5 L15.9,5.5 C15.9,6.6 16,7.9 16,9.4 L16,18 L20,18 L20,10.9 C20,10.5 20,10.2 20.1,9.9 C20.4,9.2 20.9,8.3 22,8.3 C23.4,8.3 24,9.5 24,11.1 L24,18 L28,18 L28,10.6 C28,6.9 26.1,5.2 23.6,5.2 Z"
                        id="linkedin"></path>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          </figure>
        </Link>
        <Link
          className="text-gray-500 hover:text-blue"
          href={urls.twitterShareUrl}
          target="_blank">
          <figure className=" inline-block leading-[1] mx-[7px] align-middle relative">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 30 30"
              width="21px"
              height="21px"
              fill="currentColor">
              <path d="M26.37,26l-8.795-12.822l0.015,0.012L25.52,4h-2.65l-6.46,7.48L11.28,4H4.33l8.211,11.971L12.54,15.97L3.88,26h2.65 l7.182-8.322L19.42,26H26.37z M10.23,6l12.34,18h-2.1L8.12,6H10.23z"></path>
            </svg>
          </figure>
        </Link>
        <Link
          className="text-gray-500 hover:text-blue"
          href={urls.whatsAppShareUrl}
          target="_blank">
          <figure className=" inline-block leading-[1] mx-[7px] align-middle relative">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              version="1.1"
              width="18"
              height="18"
              viewBox="0 0 256 256">
              <defs></defs>{' '}
              <g
                fill="currentColor"
                transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
                className="stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(204, 204, 204); fill-rule: nonzero; opacity: 1;">
                <path
                  d="M 76.735 13.079 C 68.315 4.649 57.117 0.005 45.187 0 C 20.605 0 0.599 20.005 0.589 44.594 c -0.003 7.86 2.05 15.532 5.953 22.296 L 0.215 90 l 23.642 -6.202 c 6.514 3.553 13.848 5.426 21.312 5.428 h 0.018 c 0.001 0 -0.001 0 0 0 c 24.579 0 44.587 -20.007 44.597 -44.597 C 89.789 32.713 85.155 21.509 76.735 13.079 z M 27.076 46.217 c -0.557 -0.744 -4.55 -6.042 -4.55 -11.527 c 0 -5.485 2.879 -8.181 3.9 -9.296 c 1.021 -1.115 2.229 -1.394 2.972 -1.394 s 1.487 0.007 2.136 0.039 c 0.684 0.035 1.603 -0.26 2.507 1.913 c 0.929 2.231 3.157 7.717 3.436 8.274 c 0.279 0.558 0.464 1.208 0.093 1.952 c -0.371 0.743 -0.557 1.208 -1.114 1.859 c -0.557 0.651 -1.17 1.453 -1.672 1.952 c -0.558 0.556 -1.139 1.159 -0.489 2.274 c 0.65 1.116 2.886 4.765 6.199 7.72 c 4.256 3.797 7.847 4.973 8.961 5.531 c 1.114 0.558 1.764 0.465 2.414 -0.279 c 0.65 -0.744 2.786 -3.254 3.529 -4.369 c 0.743 -1.115 1.486 -0.929 2.507 -0.558 c 1.022 0.372 6.5 3.068 7.614 3.625 c 1.114 0.558 1.857 0.837 2.136 1.302 c 0.279 0.465 0.279 2.696 -0.65 5.299 c -0.929 2.603 -5.381 4.979 -7.522 5.298 c -1.92 0.287 -4.349 0.407 -7.019 -0.442 c -1.618 -0.513 -3.694 -1.199 -6.353 -2.347 C 34.934 58.216 27.634 46.961 27.076 46.217 z"
                  transform=" matrix(1 0 0 1 0 0) "
                  strokeLinecap="round"
                  className="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill-rule: evenodd; opacity: 1;"></path>
              </g>
            </svg>
          </figure>
        </Link>
        <Link
          className="text-gray-500 hover:text-blue"
          href={urls.emailShareUrl}
          target="_blank">
          <figure className=" inline-block leading-[1] mx-[7px] align-middle relative">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none">
              <g id="ic:baseline-email">
                <path
                  id="Vector"
                  d="M20.5 4H4.5C3.4 4 2.51 4.9 2.51 6L2.5 18C2.5 19.1 3.4 20 4.5 20H20.5C21.6 20 22.5 19.1 22.5 18V6C22.5 4.9 21.6 4 20.5 4ZM20.5 8L12.5 13L4.5 8V6L12.5 11L20.5 6V8Z"
                  fill="currentColor"></path>
              </g>
            </svg>
          </figure>
        </Link>
      </div>
    </div>
  );
}
